import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  IconButton,
  TextField,
  Box,
  Grid,
  CircularProgress,
  Checkbox,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  Avatar,
  FormControlLabel,
  InputAdornment,
  Paper,
  Divider,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ImageIcon from '@mui/icons-material/Image';
import { accountingApi } from '../../../config/apiClient';

// Veri tipleri
interface InventoryItem {
  id: string;
  name: string;
  categoryId?: string | null;
  categoryName?: string | null; // Kategori adı (SQL'den)
  warehouseId?: string | null;
  salePrice?: number;
  stockCode?: string;
  barcode?: string;
  imageUrl?: string;
  itemType?: string;
}

interface QRMenuProductSelectionModalProps {
  open: boolean;
  onClose: () => void;
  allInventoryItems: InventoryItem[];
  selectedProductIds: string[];
  onSelectProducts: (selectedIds: string[], selectedItems?: InventoryItem[]) => void; // ✅ Seçilen ürün bilgilerini de gönder
  companyId?: string | null; // API'den ürün çekmek için gerekli
  isTicketMode?: boolean;
  prioritizeSelected?: boolean;
}

const QRMenuProductSelectionModal: React.FC<QRMenuProductSelectionModalProps> = ({
  open,
  onClose,
  allInventoryItems,
  selectedProductIds,
  onSelectProducts,
  companyId,
  isTicketMode = false,
  prioritizeSelected = false
}) => {
  // State tanımlamaları
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>(''); // Kategori filtresi
  const [currentlySelectedIds, setCurrentlySelectedIds] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [apiInventoryItems, setApiInventoryItems] = useState<InventoryItem[]>([]);
  const [apiLoading, setApiLoading] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  
  // Kategoriler için state
  const [apiCategories, setApiCategories] = useState<{ id: string; name: string }[]>([]);

  // API'den TÜM ürünleri çekme fonksiyonu (InventoryItems tablosundan)
  const fetchInventoryFromAPI = async () => {
    if (!companyId) {
      setApiError('Şirket ID bulunamadı');
      return;
    }

    setApiLoading(true);
    setApiError(null);

    try {
      console.log(`🔄 [QR MENU V2] Modal - TÜM ürünler çekiliyor: /api/Inventory/items`);
      
      // 1️⃣ TÜM ürünleri çek (InventoryItems tablosundan - sadece aktif olanlar)
      const itemsResponse = await accountingApi.get(`/api/Inventory/items`);
      const itemsResult = itemsResponse.data;
      
      const rawItems = Array.isArray(itemsResult) 
        ? itemsResult 
        : (itemsResult.data && Array.isArray(itemsResult.data) ? itemsResult.data : null);
      
      if (rawItems) {
        const activeItems = rawItems.filter((item: any) => item.isActive !== false);
        
        const itemsList: InventoryItem[] = activeItems.map((item: any) => ({
          id: item.id,
          name: item.name || 'İsimsiz Ürün',
          categoryId: item.categoryId || null,
          categoryName: item.categoryName || null,
          warehouseId: null,
          salePrice: item.salePrice || 0,
          stockCode: item.stockCode || '-',
          barcode: item.barcode || '-',
          imageUrl: item.imageUrl ? item.imageUrl.split(',')[0].trim() : undefined,
          itemType: item.itemType || item.ItemType || 'PRODUCT'
        }));
        setApiInventoryItems(itemsList);
        console.log(`✅ [QR MENU V2] Modal - ${itemsList.length} ürün yüklendi (tüm aktif ürünler)`);
      } else {
        setApiError(itemsResult.message || 'API\'den ürün listesi alınamadı (geçersiz yanıt formatı)');
      }

      // 2️⃣ Kategorileri çek
      console.log(`🔄 [QR MENU V2] Modal - Kategoriler çekiliyor: /api/Inventory/categories`);
      try {
        const categoriesResponse = await accountingApi.get(`/api/Inventory/categories`);
        const categoriesResult = categoriesResponse.data;
        
        const rawCategories = Array.isArray(categoriesResult) 
          ? categoriesResult 
          : (categoriesResult.data && Array.isArray(categoriesResult.data) ? categoriesResult.data : null);
        
        if (rawCategories) {
          const categoriesList = rawCategories.map((cat: any) => ({
            id: cat.id,
            name: cat.name || 'İsimsiz Kategori'
          }));
          setApiCategories(categoriesList);
          console.log(`✅ [QR MENU V2] Modal - ${categoriesList.length} kategori yüklendi`);
        }
      } catch (catError: any) {
        console.warn(`⚠️ [QR MENU V2] Modal - Kategoriler yüklenemedi: ${catError.response?.status}`);
      }
    } catch (error) {
      console.error('❌ [QR MENU V2] Modal - API bağlantı hatası:', error);
      setApiError('API bağlantı hatası oluştu');
    } finally {
      setApiLoading(false);
    }
  };

  // Modal açıldığında seçili ürünleri yükle ve API'den ürünleri çek
  useEffect(() => {
    if (open) {
      setCurrentlySelectedIds([...selectedProductIds]);
      setPage(0);
      setSearchQuery('');
      setSelectedCategory(''); // Kategori filtresini sıfırla
      
      // Eğer companyId varsa API'den ürünleri çek
      if (companyId) {
        fetchInventoryFromAPI();
      }
    }
  }, [open, selectedProductIds, companyId]);
  
  // API'den gelen ürünleri kullan, yoksa prop'tan gelen ürünleri kullan
  const sourceInventoryItems = companyId ? apiInventoryItems : allInventoryItems;
  const inventoryItems = isTicketMode
    ? sourceInventoryItems.filter(item => String(item.itemType || '').toUpperCase() === 'SERVICE')
    : sourceInventoryItems;
  
  // Benzersiz kategorileri API'den gelen kategorilerden veya ürünlerden çıkar
  const uniqueCategories = companyId && apiCategories.length > 0
    ? apiCategories // SQL'den gelen kategoriler
    : Array.from(
        new Set(
          inventoryItems
            .map(item => item.categoryId)
            .filter((category): category is string => category != null && category.trim() !== '')
        )
      ).map(catId => ({ id: catId, name: catId })).sort((a, b) => a.name.localeCompare(b.name));
  
  // Arama sorgusuna ve kategoriye göre filtrelenmiş ürünler
  const filteredProducts = inventoryItems.filter(item => {
    // Arama filtresi
    const query = searchQuery.toLowerCase().trim();
    const matchesSearch = !query || (
      item.name.toLowerCase().includes(query) ||
      (item.stockCode && item.stockCode.toLowerCase().includes(query)) ||
      (item.barcode && item.barcode.toLowerCase().includes(query))
    );
    
    // Kategori filtresi
    const matchesCategory = !selectedCategory || item.categoryId === selectedCategory;
    
    return matchesSearch && matchesCategory;
  });

  const orderedFilteredProducts = prioritizeSelected
    ? [...filteredProducts].sort((a, b) => {
        const aSelected = currentlySelectedIds.includes(a.id);
        const bSelected = currentlySelectedIds.includes(b.id);
        if (aSelected === bSelected) return 0;
        return aSelected ? -1 : 1;
      })
    : filteredProducts;
  
  // Sayfalandırılmış ürünler
  const paginatedProducts = orderedFilteredProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );
  
  // Sayfa değiştirme
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  // Sayfa başına satır sayısı değiştirme
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Arama veya kategori değiştiğinde sayfa 0'a dön
  useEffect(() => {
    setPage(0);
  }, [searchQuery, selectedCategory]);

  // Ticket modunda service disi secimleri otomatik temizle.
  useEffect(() => {
    if (!open || !isTicketMode) return;
    const allowedIds = new Set(inventoryItems.map(item => item.id));
    setCurrentlySelectedIds(prev => {
      const filtered = prev.filter(id => allowedIds.has(id));
      return filtered.length === prev.length ? prev : filtered;
    });
  }, [open, isTicketMode, inventoryItems]);
  
  // Ürün seçme
  const handleToggleProduct = (productId: string) => {
    setCurrentlySelectedIds(prev => {
      if (prev.includes(productId)) {
        return prev.filter(id => id !== productId);
      } else {
        return [...prev, productId];
      }
    });
  };
  
  // Tüm ürünleri seç/kaldır
  const handleToggleAll = () => {
    if (paginatedProducts.every(product => currentlySelectedIds.includes(product.id))) {
      // Tüm ürünler seçiliyse, hepsini kaldır
      setCurrentlySelectedIds(prev => 
        prev.filter(id => !paginatedProducts.some(product => product.id === id))
      );
    } else {
      // Hepsi seçili değilse, hepsini ekle
      const newSelectedIds = [...currentlySelectedIds];
      paginatedProducts.forEach(product => {
        if (!newSelectedIds.includes(product.id)) {
          newSelectedIds.push(product.id);
        }
      });
      setCurrentlySelectedIds(newSelectedIds);
    }
  };
  
  // Seçimi kaydet
  const handleSave = () => {
    const allowedIds = new Set(inventoryItems.map(item => item.id));
    const idsToSave = currentlySelectedIds.filter(id => allowedIds.has(id));

    // ✅ Seçilen ürünlerin tam bilgilerini de gönder (inventoryItems'dan al)
    const selectedItems = idsToSave
      .map(id => inventoryItems.find(item => item.id === id))
      .filter((item): item is InventoryItem => !!item);
    
    console.log('✅ [MODAL] Seçilen ürünler gönderiliyor:', {
      ids: idsToSave,
      items: selectedItems.map(i => ({ id: i.id, name: i.name }))
    });
    
    onSelectProducts(idsToSave, selectedItems);
    onClose();
  };
  
  // İptal et
  const handleCancel = () => {
    onClose();
  };
  
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: 'primary.main', color: 'white', py: 1, px: 2 }}>
        <Typography variant="subtitle1">{isTicketMode ? 'Bilet Türü Seçimi' : 'Ürün Seçimi'}</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2 }}>
          {/* Arama ve Kategori Filtreleri */}
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                placeholder={isTicketMode ? 'Bilet türü ara... (Ad, stok kodu veya barkod)' : 'Ürün ara... (Ürün adı, stok kodu veya barkod)'}
                variant="outlined"
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="category-filter-label">Kategori</InputLabel>
                <Select
                  labelId="category-filter-label"
                  id="category-filter"
                  value={selectedCategory}
                  label="Kategori"
                  onChange={(e) => setSelectedCategory(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Tüm Kategoriler</em>
                  </MenuItem>
                  {uniqueCategories.map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      {category.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="body2" color="textSecondary">
              {filteredProducts.length} {isTicketMode ? 'bilet türü' : 'ürün'} bulundu{(searchQuery || selectedCategory) && ` (${inventoryItems.length} toplam)`}, {currentlySelectedIds.length} {isTicketMode ? 'bilet türü' : 'ürün'} seçili
              {selectedCategory && (
                <Typography component="span" variant="body2" sx={{ ml: 1, color: 'primary.main', fontWeight: 'medium' }}>
                  • {uniqueCategories.find(c => c.id === selectedCategory)?.name || selectedCategory}
                </Typography>
              )}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={paginatedProducts.length > 0 && paginatedProducts.every(product => currentlySelectedIds.includes(product.id))}
                  indeterminate={
                    paginatedProducts.some(product => currentlySelectedIds.includes(product.id)) &&
                    !paginatedProducts.every(product => currentlySelectedIds.includes(product.id))
                  }
                  onChange={handleToggleAll}
                  size="small"
                />
              }
              label="Tümünü Seç"
            />
          </Box>
          
          {(loading || apiLoading) ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ ml: 2, alignSelf: 'center' }}>
                {apiLoading ? (isTicketMode ? 'Bilet türleri yükleniyor...' : 'Ürünler yükleniyor...') : 'Yükleniyor...'}
              </Typography>
            </Box>
          ) : apiError ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <Typography variant="body2" color="error">
                {apiError}
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400, overflowY: 'auto' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={paginatedProducts.length > 0 && paginatedProducts.every(product => currentlySelectedIds.includes(product.id))}
                          indeterminate={
                            paginatedProducts.some(product => currentlySelectedIds.includes(product.id)) &&
                            !paginatedProducts.every(product => currentlySelectedIds.includes(product.id))
                          }
                          onChange={handleToggleAll}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{isTicketMode ? 'Bilet Türü' : 'Ürün Adı'}</TableCell>
                      <TableCell>Kategori</TableCell>
                      <TableCell>Stok Kodu</TableCell>
                      <TableCell>Barkod</TableCell>
                      <TableCell align="right">Fiyatı</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedProducts.map((product) => (
                      <TableRow 
                        key={product.id}
                        hover
                        onClick={() => handleToggleProduct(product.id)}
                        selected={currentlySelectedIds.includes(product.id)}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={currentlySelectedIds.includes(product.id)}
                            onChange={() => handleToggleProduct(product.id)}
                            size="small"
                            onClick={(e) => e.stopPropagation()}
                          />
                        </TableCell>
                        <TableCell component="th" scope="row" sx={{ display: 'flex', alignItems: 'center' }}>
                          <Box sx={{ display: 'inline-flex', marginRight: 1, alignItems: 'center' }}>
                            {product.imageUrl ? (
                              <Avatar 
                                src={product.imageUrl}
                                alt={product.name}
                                variant="rounded"
                                sx={{ 
                                  width: 32, 
                                  height: 32,
                                  border: '1px solid #eee',
                                  objectFit: 'contain'
                                }}
                              />
                            ) : (
                              <Avatar 
                                variant="rounded"
                                sx={{ 
                                  width: 32, 
                                  height: 32, 
                                  bgcolor: 'grey.100',
                                  border: '1px solid #eee'
                                }}
                              >
                                <ImageIcon fontSize="small" sx={{ color: 'grey.500' }} />
                              </Avatar>
                            )}
                          </Box>
                          {product.name}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ 
                            color: (product.categoryName || product.categoryId) ? 'text.primary' : 'text.secondary',
                            fontStyle: (product.categoryName || product.categoryId) ? 'normal' : 'italic'
                          }}>
                            {product.categoryName || (product.categoryId ? uniqueCategories.find(c => c.id === product.categoryId)?.name : null) || 'Kategorisiz'}
                          </Typography>
                        </TableCell>
                        <TableCell>{product.stockCode}</TableCell>
                        <TableCell>{product.barcode}</TableCell>
                        <TableCell align="right">
                          {product.salePrice?.toLocaleString('tr-TR', { style: 'currency', currency: 'TRY' }) ?? '-'}
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {paginatedProducts.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                          {(searchQuery || selectedCategory)
                            ? (isTicketMode ? 'Filtreleme kriterlerine uygun bilet türü bulunamadı.' : 'Filtreleme kriterlerine uygun ürün bulunamadı.')
                            : (isTicketMode ? 'Henüz bilet türü eklenmemiş.' : 'Henüz ürün eklenmemiş.')}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={filteredProducts.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50, 100]}
                labelRowsPerPage={isTicketMode ? 'Sayfa başına bilet türü:' : 'Sayfa başına ürün:'}
                labelDisplayedRows={({ from, to, count }) => `${count} ${isTicketMode ? 'bilet türünden' : 'üründen'} ${from}-${to} arası`}
              />
            </>
          )}
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Typography variant="body2" color="textSecondary" sx={{ flexGrow: 1 }}>
          {currentlySelectedIds.length} {isTicketMode ? 'bilet türü' : 'ürün'} seçili
        </Typography>
        <Button onClick={handleCancel} color="inherit">
          İptal
        </Button>
        <Button 
          onClick={handleSave} 
          variant="contained" 
          color="primary"
        >
          {isTicketMode ? 'Seçili Bilet Türlerini Ekle' : 'Seçili Ürünleri Ekle'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QRMenuProductSelectionModal; 